import { RequiredTupleOf, ValueOf } from "shared/lib/typeCheck.lib";

type QueryParamValue = string;
type QueryParamKey<T extends QueryParamObject> = keyof T;
type QueryParamObject = Record<string, QueryParamValue>;

const BehaviourType = {
  Query: "query",
  Hash: "hash",
} as const;

export class QueryParamsManager<T extends QueryParamObject, K extends keyof QueryParamObject = keyof QueryParamObject> {
  private nonDeletableParams = new Set<string>();
  private urlBehaviour: ValueOf<typeof BehaviourType>;

  constructor(
    private url: string,
    private posibleUrlParams: RequiredTupleOf<K>,
    optionalParams?: {
      nonDeletableParams: K[];
      urlBehaviour: ValueOf<typeof BehaviourType>;
    }
  ) {
    this.url = url;
    // TODO: resolve TS error: "error TS2589: Type instantiation is excessively deep and possibly infinite."
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.posibleUrlParams = posibleUrlParams;
    this.nonDeletableParams = new Set(optionalParams?.nonDeletableParams ?? []);
    this.urlBehaviour = optionalParams?.urlBehaviour ?? BehaviourType.Query;
  }

  public set<K extends QueryParamKey<T>, V extends QueryParamValue>(key: K, value: V): void {
    const normalizedKey = this.normalizeKey(key);
    const encodedValue = encodeURIComponent(value);

    if (this.urlBehaviour === BehaviourType.Hash) {
      const { baseUrl, hash, searchParams } = this.parseHashUrl(this.url);
      searchParams.set(normalizedKey, encodedValue);
      this.url = this.buildHashUrl(baseUrl, hash, searchParams);
    } else {
      const urlObj = new URL(this.url);
      urlObj.searchParams.set(normalizedKey, encodedValue);
      this.setUrl(urlObj);
    }
  }

  public setAll(obj: T): void {
    for (const [key, value] of Object.entries(obj)) {
      this.set(key, value);
    }
  }

  public get<K extends QueryParamKey<T>>(key: K): T[K] | null {
    const normalizedKey = this.normalizeKey(key);

    if (this.urlBehaviour === BehaviourType.Hash) {
      const { searchParams } = this.parseHashUrl(this.url);
      return searchParams.get(normalizedKey) as T[K] | null;
    } else {
      const urlObj = new URL(this.url);
      return urlObj.searchParams.get(normalizedKey) as T[K] | null;
    }
  }

  public getAll(): T | null {
    const searchParams =
      this.urlBehaviour === BehaviourType.Hash
        ? this.parseHashUrl(this.url).searchParams
        : new URL(this.url).searchParams;

    const params = {} as T;

    for (const [key, value] of searchParams.entries() as IterableIterator<[QueryParamKey<T>, T[QueryParamKey<T>]]>) {
      params[key] = value;
    }

    return Object.keys(params).length ? params : null;
  }

  public remove(key: QueryParamKey<T>): void {
    const normalizedKey = this.normalizeKey(key);

    if (this.nonDeletableParams.has(normalizedKey)) {
      return;
    }

    if (this.urlBehaviour === BehaviourType.Hash) {
      const { baseUrl, hash, searchParams } = this.parseHashUrl(this.url);
      if (searchParams.has(normalizedKey)) {
        searchParams.delete(normalizedKey);
      }
      this.url = this.buildHashUrl(baseUrl, hash, searchParams);
    } else {
      const urlObj = new URL(this.url);
      if (urlObj.searchParams.has(normalizedKey)) {
        urlObj.searchParams.delete(normalizedKey);
      }
      this.setUrl(urlObj);
    }
  }

  public clear(): void {
    const castedPosibleUrlParams = Array.from(this.posibleUrlParams) as K[];
    if (!castedPosibleUrlParams.length) return;

    if (this.urlBehaviour === BehaviourType.Hash) {
      const { baseUrl, hash, searchParams } = this.parseHashUrl(this.url);
      castedPosibleUrlParams.forEach((param) => {
        if (this.nonDeletableParams.has(String(param))) return;

        if (searchParams.has(param)) {
          searchParams.delete(param);
        }
      });
      this.url = this.buildHashUrl(baseUrl, hash, searchParams);
    } else {
      const urlObj = new URL(this.url);
      castedPosibleUrlParams.forEach((param) => {
        if (this.nonDeletableParams.has(String(param))) return;

        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.delete(param);
        }
      });
      this.setUrl(urlObj);
    }
  }

  public build(): string {
    return this.url;
  }

  public update({ state = window.history.state }: { state?: unknown } = {}): void {
    window.history.replaceState(state, "", this.build());
  }

  public updateBaseUrl(newUrl: string): void {
    this.url = newUrl;
  }

  private setUrl(url: URL): void {
    this.url = url.toString();
  }

  private normalizeKey(key: QueryParamKey<T>): string {
    return String(key);
  }

  /**
   * Parses URL with hash router
   * Format: http://example.com/?parent=params#/path?our=params
   *
   * IMPORTANT: This method preserves everything BEFORE the hash (#) unchanged.
   * It only extracts and works with parameters AFTER the hash.
   *
   * Handles both correct format (#/path?param=value) and malformed format (#/path&param=value)
   */
  private parseHashUrl(url: string): { baseUrl: string; hash: string; searchParams: URLSearchParams } {
    const urlObj = new URL(url);
    const hashPart = urlObj.hash.slice(1); // remove #

    // Find the first occurrence of ? or & to separate path from params
    // Prefer ? (correct format), but handle & (malformed format) as fallback
    const questionMarkIndex = hashPart.indexOf("?");
    const ampersandIndex = hashPart.indexOf("&");

    // Use the first separator found (prefer ? over &)
    const separatorIndex = questionMarkIndex !== -1 ? questionMarkIndex : ampersandIndex !== -1 ? ampersandIndex : -1;

    if (separatorIndex === -1) {
      // no query parameters in hash
      return {
        baseUrl: urlObj.origin + urlObj.pathname + urlObj.search, // Preserve everything before # (including parent app's query params)
        hash: hashPart,
        searchParams: new URLSearchParams(),
      };
    }

    const hash = hashPart.slice(0, separatorIndex);
    const queryString = hashPart.slice(separatorIndex + 1);

    return {
      baseUrl: urlObj.origin + urlObj.pathname + urlObj.search, // Preserve everything before # (including parent app's query params)
      hash,
      searchParams: new URLSearchParams(queryString), // URLSearchParams handles both ?param=value and &param=value formats
    };
  }

  /**
   * Builds URL with hash router
   * Format: http://example.com/?parent=params#/path?our=params
   *
   * IMPORTANT: This method receives baseUrl (everything before #) as-is and never modifies it.
   * It only adds the hash part with our parameters.
   */
  private buildHashUrl(baseUrl: string, hash: string, searchParams: URLSearchParams): string {
    const urlObj = new URL(baseUrl); // baseUrl already contains parent app's query params
    const queryString = searchParams.toString();
    // Build hash with query params: /path?param=value
    const hashWithParams = queryString ? `${hash}?${queryString}` : hash;
    // Set hash (URL automatically adds # if needed)
    urlObj.hash = hashWithParams;
    return urlObj.href; // Returns URL with baseUrl unchanged + our hash params
  }
}
